import { colors } from 'fitify-ui'
import { StyledIcon } from 'fitify-ui/src/Icon/Icon.Styled'
import Link from 'next/link'
import { css, styled } from 'styled-components'

type ButtonVariant = 'primary' | 'secondary'
type ButtonSize = 'default' | 'small'

const baseStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Graphik LCG Web', sans-serif;
  font-size: 16px;
  font-style: normal;
  text-decoration: none;
  transition: all 0.2s ease;

  &:disabled {
    box-shadow: none;
    cursor: not-allowed;
  }
`

const primaryStyles = css`
  border-color: ${colors.Blue400};
  background-color: ${colors.Blue400};
  color: ${colors.White};
  font-weight: 600;

  @media (hover: hover) {
    &:hover,
    &:focus {
      border-color: ${colors.Blue400};
      background-color: ${colors.Blue600};
      box-shadow: 0 4px 12px rgb(0 0 0 / 15%);
    }
  }

  &:active {
    box-shadow: 0 2px 4px rgb(0 0 0 / 10%);
  }

  &:disabled {
    border-color: ${colors.Gray300};
    background-color: ${colors.Gray300};
  }
`

const secondaryStyles = css`
  border-color: ${colors.Gray300};
  background-color: transparent;
  color: ${colors.Gray700};
  font-weight: 500;

  @media (hover: hover) {
    &:hover,
    &:focus {
      border-color: ${colors.Blue400};
      background-color: ${colors.Blue50};
      box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
      color: ${colors.Blue600};
    }
  }

  &:active {
    box-shadow: 0 2px 4px rgb(0 0 0 / 5%);
  }

  &:disabled {
    border-color: ${colors.Gray200};
    background-color: transparent;
    color: ${colors.Gray400};
  }
`

const sizeStyles = {
  default: css`
    padding: 14px 28px;

    @media (max-width: 768px) {
      width: 100%;
      padding: 16px 28px;
    }
  `,
  small: css`
    padding: 8px 16px;
    font-size: 14px;
  `,
}

const variantStyles = {
  primary: primaryStyles,
  secondary: secondaryStyles,
}

export const StyledButton = styled.button<{
  $variant: ButtonVariant
  $size: ButtonSize
  $isLoading: boolean
}>`
  ${baseStyles};
  ${({ $variant }) => variantStyles[$variant]};
  ${({ $size }) => sizeStyles[$size]};

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      ${StyledIcon} {
        position: absolute;
        width: 20px;
        height: 20px;
        color: currentcolor;
      }
    `};
`

export const StyledButtonContent = styled.span<{ $isLoading: boolean }>`
  visibility: ${({ $isLoading }) => ($isLoading ? 'hidden' : 'visible')};
`

export const StyledButtonLink = styled(Link)<{
  $variant: ButtonVariant
  $size: ButtonSize
}>`
  ${baseStyles};
  ${({ $variant }) => variantStyles[$variant]};
  ${({ $size }) => sizeStyles[$size]};
`
